import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../Head';
import Hero from '../Hero';
import * as css from './Development.m.css';
import * as commonCss from '../../Common.m.css';
import * as industryCss from '../IndustryExpertise.m.css';
import Summary from '../Summary';
import ConnectStrip from '../ConnectStrip';
import FAQ from '../FAQ';

const analysisImg = require('../../assets/images/services/analysis.svg');
const consultingImg = require('../../assets/images/services/consulting.svg');
const researchImg = require('../../assets/images/development/enterprise-ux-research.svg');
const rapidImg = require('../../assets/images/development/rapid-prototyping-design.svg');
const uiDesignImg = require('../../assets/images/development/enterprise-app-ui-design.svg');
const uxDesignImg = require('../../assets/images/development/enterprise-software-ux-design.svg');

const leadingTop = require('../../assets/images/development/design/leadingTop.svg');
const leadingMiddle = require('../../assets/images/development/design/leadingMiddle.svg');
const leadingBottom = require('../../assets/images/development/design/leadingBottom.svg');
const trailingTop = require('../../assets/images/development/design/trailingTop.svg');
const trailingMiddle = require('../../assets/images/development/design/trailingMiddle.svg');
const trailingBottom = require('../../assets/images/development/design/trailingBottom.svg');

const factory = create();

export const EnterpriseUX = factory(function EnterpriseUX() {
	return (
		<div classes={css.root}>
			<Head
				title="Enterprise UX UI Design Services | SitePen"
				description="Enterprise UI UX design and development services to improve processes, streamline operations, and empower business success."
			/>
			<div classes={[css.heroWrapper]}>
				<div classes={commonCss.contentWrapper}>
					<Hero>
						{{
							xlTitle: 'Enterprise UX'
						}}
					</Hero>
				</div>
			</div>
			<section classes={commonCss.section}>
				<div classes={commonCss.contentWrapper}>
					<Summary reverse collapse="hide" margin={false}>
						{{
							title: 'Perfect Your Product with Enterprise Software UX Design',
							blurb: (
								<p>
									Enterprise software has generated a revolutionary increase in
									efficiency and effectiveness for every major company in the
									world. The move from paper-based systems to software-driven
									architectures allowed companies to streamline their processes
									and increase their ability to react to changing market
									conditions. In today's world, however, merely having
									software-based systems isn't enough to maintain an enterprise's
									competitive advantage. User Experience (UX) design is about
									increasing user efficiency and minimizing human error; this is
									accomplished by designing each application's interface to
									support and guide users through their interactions.
								</p>
							),
							image: (
								<img
									src={consultingImg}
									classes={commonCss.responsiveImage}
									alt=""
								/>
							)
						}}
					</Summary>
				</div>
			</section>
			<section classes={[commonCss.shaded, commonCss.section]}>
				<div classes={commonCss.contentWrapper}>
					<h2>Our Enterprise Design Services</h2>
					<p classes={commonCss.largeMarginBottom}>
						Our agency considers the entire person-machine interface to deliver the
						ideal design for each system.
					</p>
				</div>
				<div classes={commonCss.smallContentWrapper}>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Enterprise UX Research',
							blurb: `UX design requires more than a deep understanding of the underlying business processes. Optimized designs require deep and on-going learning about how people want to interact with software. Our designers monitor these trends to ensure that each user's experience benefits from the latest research in visual design, information architecture, interaction design, and usability to provide an experience that feels natural and obvious.`,
							image: (
								<img
									src={researchImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Rapid Prototyping & Design',
							blurb: `Software systems start as a product of a few minds but eventually need to be realized as applications that are accessible by many users. Our rapid prototyping and design capabilities allow initial design concepts and information flows to be quickly developed and iterated upon. These capabilities accelerate the design process and help guarantee that end-users receive a system optimized for their needs.`,
							image: (
								<img
									src={rapidImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller>
						{{
							title: 'Enterprise Application UI Design',
							blurb: `Our enterprise user-interface design services extend far beyond getting the information to the user and allowing them to interact with it. Page layout, size, color, and animations are optimized to ensure that the user's eye is drawn to the most critical UI elements first while also providing them easy access to any supporting information that they need to quickly and accurately make essential business decisions.`,
							image: (
								<img
									src={uiDesignImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
					<Summary trailingBias="large" smaller margin={false}>
						{{
							title: 'Enterprise Software UX Design',
							blurb: `UX design is ultimately about mapping user behavior to software systems. We design user experiences that consider visual design, interaction design, information architecture, usability, and accessibility to provide solutions that present shallow learning curves and are frictionless to use.`,
							image: (
								<img
									src={uxDesignImg}
									classes={commonCss.iconMedium}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
				</div>
			</section>
			<section classes={[commonCss.section, industryCss.industrySmall]}>
				<div classes={industryCss.industryLeading}>
					<img src={leadingTop} classes={industryCss.image} alt="" loading="lazy" />
					<img
						src={leadingMiddle}
						classes={[industryCss.image, industryCss.industryMiddle]}
						alt=""
						loading="lazy"
					/>
					<img src={leadingBottom} classes={industryCss.image} alt="" loading="lazy" />
				</div>
				<div classes={industryCss.contentWrapper}>
					<h2>Our UI &amp; UX Design Process</h2>
					<p>
						Our agency's UX design process is composed of two major elements -
						maintaining a thorough understanding of current design trends and applying
						that knowledge to each system's unique requirements.
					</p>
					<p>
						The design team uses a holistic approach to keep abreast of the latest
						best-practices in UX design. Our designers study how the brain processes
						information and makes decisions. This grants them a robust theoretical
						understanding of how to engage users best. They also follow blogs and
						podcasts that focus on UX frameworks and how these tools are shaping users'
						expectations for how applications function. Finally, all of our designers
						have a wealth of practical experience designing enterprise applications.
						This experience grants them critical insights on what new ideas will be most
						effective when applied to enterprise applications.
					</p>
					<p>
						Having solid foundational knowledge is a critical component of effective UX
						design. Equally important, however, is the need to understand the
						intricacies of the problem that each application is intended to address. Our
						designers will work with you to understand and document the workflows and
						business processes that the software must support. This iterative process
						allows the designers to distill the nebulous concepts in UX design into a
						concrete and productive experience for your end-users.
					</p>
				</div>
				<div classes={industryCss.industryTrailing}>
					<img src={trailingTop} classes={industryCss.image} alt="" loading="lazy" />
					<img
						src={trailingMiddle}
						classes={[industryCss.image, industryCss.industryMiddle]}
						alt=""
						loading="lazy"
					/>
					<img src={trailingBottom} classes={industryCss.image} alt="" loading="lazy" />
				</div>
			</section>
			<section classes={[commonCss.section, commonCss.shaded]}>
				<div classes={commonCss.contentWrapper}>
					<Summary trailingBias="medium" margin={false}>
						{{
							title: 'Why Good Enterprise Application UX Matters',
							blurb: (
								<virtual>
									<p>
										When software was first introduced to the enterprise, it
										brought a revolutionary increase in speed and a decrease in
										human errors by leveraging software's ability to codify
										rules and business processes. Unfortunately, these first
										applications were often challenging to use and even more
										difficult to learn. As business processes have become more
										complicated and employee turnover has increased, these
										liabilities have become increasingly burdensome. Effective
										and efficient UX design offers another opportunity to
										streamline business processes, reduce errors, and reduce
										user frustration.
									</p>
									<ul classes={css.bullets}>
										<li classes={css.bulletItem}>
											A robust visual design allows users to identify critical
											information quickly
										</li>
										<li classes={css.bulletItem}>
											Well-designed information architecture exposes precisely
											the information that users need to see when, and only
											when, they need it.
										</li>
										<li classes={css.bulletItem}>
											Usability enhances the effectiveness and efficiency of
											the interface
										</li>
										<li classes={css.bulletItem}>
											Accessibility allows the system to be easily
											comprehended and used by the broadest possible
											population of users without loss of productivity or
											increased frustration
										</li>
									</ul>
								</virtual>
							),
							image: (
								<img
									src={analysisImg}
									classes={commonCss.responsiveImage}
									alt=""
									loading="lazy"
								/>
							)
						}}
					</Summary>
				</div>
			</section>
			<ConnectStrip />
			<FAQ title="Enterprise FAQs">
				{[
					{
						title: 'What is Enterprise UX?',
						content: (
							<p>
								Enterprise UX is the combination of visual design, information
								architecture, interaction design, accessibility, and usability that
								makes up the human-computer interactions of an application. Ideally,
								the UX design should support each user's behavior and expectations
								by providing the information and actions that they need as they
								progress through one or more workflows.
							</p>
						)
					},
					{
						title: `What is the Enterprise UX Design Process?`,
						content: (
							<p>
								The user experience design process comprises the steps and
								methodologies used to combine visual and interaction design,
								information architecture, accessibility, and usability into a system
								that defines how users and systems interact. Designers begin with a
								large body of general knowledge concerning how to optimize a
								system's human-computer interactions. The process focuses that
								knowledge onto the specific requirements of the system being
								designed to deliver a solution that is efficient and resistant to
								human error.
							</p>
						)
					},
					{
						title: 'What Enterprise UX Solutions Do You Offer?',
						content: (
							<virtual>
								<p>We offer a wide range of user experience design solutions.</p>
								<ul>
									<li>
										<b>End-to-end development</b>
										<p>
											This solution engages both our design and engineering
											teams to provide a complete software solution. We
											partner with you at each step of the process to
											understand your needs and ensure that our solution
											integrates smoothly into your existing software
											ecosystem while delivering an exceptional experience for
											your users.
										</p>
									</li>
									<li>
										<b>Greenfield Design</b>
										<p>
											Many enterprises already have strong engineering groups
											and the capacity to support new application development.
											For these companies, we offer stand-alone design
											services. Our designers will work closely with the user
											community to ensure that the UX meets their needs. As
											the software is developed, they provide critical
											feedback that will guarantee that the UX of the final
											product remains focused and productive.
										</p>
									</li>
									<li>
										<b>Legacy Application Support</b>
										<p>
											Enterprise applications evolve throughout their
											lifetimes and often need to be re-tuned to support
											changing user expectations and business needs. Our
											designers can approach these projects from multiple
											directions. They can work with your organization to
											completely redesign an application's UX or help minimize
											the impact on the application's design while allowing
											new features to be integrated.
										</p>
									</li>
								</ul>
							</virtual>
						)
					},
					{
						title: `What are the Challenges of Enterprise UX?`,
						content: (
							<p>
								The most common problem with UX design is overwhelming the user with
								information and options. Engineering teams are often composed of
								individuals that are comfortable filtering through vast amounts of
								information in their daily work. When these same teams are tasked
								with designing application interfaces, this ability often translates
								into systems with too much information and too many actions. Good UX
								design minimizes the clutter that a user must work through while
								still providing access to supporting information and actions when
								they are needed.
							</p>
						)
					}
				]}
			</FAQ>
		</div>
	);
});

export default EnterpriseUX;
